import React, { useMemo } from 'react';
import {
  RCPT_CALLS_WORTH_REVIEWING,
  RCPT_EARLY_PATTERNS,
  RCPT_NEEDS_ATTENTION,
  RCPT_SAFETY_CHECKS,
  RCPT_TRIAL,
  RCPT_TRIAL_PROGRESS_INCLUDES,
  RCPT_WORK_COMPLETED,
} from '../../receptionist-dashboard/mockData';
import type { AttentionItem, TrialReviewCall, WorkCompletedItem } from '../../receptionist-dashboard/types';
import { fmtDuration, seriesTailDelta, useLiveDuration } from '../../receptionist-dashboard/utils';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { DashboardKpiCell } from './shared/DashboardKpiCell';
import { DashboardPill } from './shared/DashboardPill';
import { DashboardSectionHead } from './shared/DashboardSectionHead';
import { PriorityBadge } from './shared/PriorityBadge';
import './receptionist-empty.css';
import './receptionist-trial.css';

const TRIAL_GRAD_ID = 'rcpt-trial-grad';

function BigWaveform({ bars = 64 }: { bars?: number }) {
  const heights = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < bars; i++) {
      const seed = (Math.sin(i * 1.37) + Math.cos(i * 0.71)) * 0.5 + 0.5;
      arr.push(0.18 + seed * 0.78);
    }
    return arr;
  }, [bars]);

  return (
    <div className="flex h-[76px] w-full items-center gap-[3px]">
      {heights.map((h, i) => {
        const center = Math.abs(i - bars / 2) / (bars / 2);
        const isHot = center < 0.5;
        return (
          <span
            key={i}
            className="rcpt-trial-wave w-[3px] rounded-[1.5px]"
            style={{
              height: `${h * 100}%`,
              background: isHot ? 'var(--ai-3)' : 'var(--cream-shadow)',
              opacity: isHot ? 1 - center * 0.5 : 0.85,
              animationDelay: `${(i * 60) % 1800}ms`,
              boxShadow: isHot ? '0 0 8px color-mix(in srgb, var(--ai-3) 25%, transparent)' : 'none',
            }}
          />
        );
      })}
    </div>
  );
}

function LiveCallHero() {
  const liveNow = RCPT_TRIAL.liveNow;
  const dur = useLiveDuration(liveNow.duration);

  return (
    <div className="relative min-w-0 flex-[1.5] overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-sm">
      <div className="rcpt-empty-aurora-1" />
      <div className="rcpt-empty-aurora-2" />
      <div className="rcpt-empty-aurora-3" />
      <div className="rcpt-trial-live-rim" />

      <div className="relative z-[1] px-[26px] py-[22px] pb-6">
        <div className="mb-[18px] flex items-center gap-2.5">
          <span className="elva-live" />
          <span className="font-[var(--font-mono)] text-[10.5px] font-medium uppercase tracking-[0.14em] text-[var(--ai-3)]">
            Live · on call
          </span>
          <span className="text-[var(--ink-4)]">·</span>
          <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">{liveNow.intent}</span>
          <div className="flex-1" />
          <DashboardPill variant="neutral">
            <Icon kind="team" size={10} stroke={2} />
            {liveNow.patient.split('·')[0]?.trim()}
          </DashboardPill>
        </div>

        <div className="mb-2 flex items-end gap-[18px]">
          <span className="font-[var(--font-display)] text-[84px] font-extrabold leading-[0.9] tracking-[-0.06em] tabular-nums text-[var(--ink-0)]">
            {fmtDuration(dur)}
          </span>
          <div className="pb-2.5">
            <div className="font-[var(--font-mono)] text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink-4)]">
              call duration
            </div>
            <DashboardPill className="mt-1.5">
              <Icon kind="check" size={10} stroke={2.5} />
              Booking intent
            </DashboardPill>
          </div>
          <div className="flex-1" />

          <div
            className="relative flex h-[76px] w-[220px] shrink-0 flex-col justify-end overflow-hidden border-l border-[var(--cream-2)] pl-3.5"
            style={{
              maskImage: 'linear-gradient(transparent, #000 30%, #000 70%, transparent)',
              WebkitMaskImage: 'linear-gradient(transparent, #000 30%, #000 70%, transparent)',
            }}
          >
            <div className="rcpt-trial-marquee flex flex-col gap-[7px]">
              {[...liveNow.transcript, ...liveNow.transcript].map((line, i) => (
                <div key={i} className="flex items-start gap-[7px]">
                  <span
                    className="pt-0.5 font-[var(--font-mono)] text-[9.5px] font-semibold tracking-[0.05em]"
                    style={{ color: line.who === 'elva' ? 'var(--ai-3)' : 'var(--ink-4)' }}
                  >
                    {line.who === 'elva' ? 'ELVA' : 'CALL'}
                  </span>
                  <span className="text-[11.5px] leading-snug tracking-[-0.005em] text-[var(--ink-2)]">
                    {line.t}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="my-[18px]">
          <BigWaveform />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[10px] border border-[var(--ai-3)] bg-[var(--ai-3)] px-4 text-[13px] font-semibold text-white shadow-[0_0_0_1px_var(--ai-tint),0_6px_18px_var(--ai-tint)]"
          >
            <Icon kind="headphones" size={13} stroke={2} />
            Listen in
          </button>
          <button
            type="button"
            className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[10px] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-3.5 text-[13px] font-medium text-[var(--ink-0)] hover:bg-[var(--cream-1)]"
          >
            <Icon kind="chat" size={13} stroke={2} />
            View transcript
          </button>
          <button
            type="button"
            className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[10px] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-3.5 text-[13px] font-medium text-[var(--ink-0)] hover:bg-[var(--cream-1)]"
          >
            <Icon kind="team" size={13} stroke={2} />
            Take over
          </button>
          <div className="flex-1" />
          <div className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">
            <span className="text-[var(--ink-3)]">+1 (415) 555-0182</span> · routed via main line
          </div>
        </div>
      </div>
    </div>
  );
}

function SinceLaunchTotals() {
  const data = RCPT_TRIAL;
  const pct = Math.round((data.completedByElva / data.callsHandled) * 100);

  return (
    <div className="relative w-[340px] shrink-0 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-sm">
      <div className="rcpt-empty-aurora-3 opacity-20" />
      <div className="relative z-[1] flex h-full flex-col px-[22px] py-5 pb-[22px]">
        <div className="mb-3.5 flex items-center gap-2">
          <Icon kind="sparkle" size={14} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Since launch
          </span>
          <div className="flex-1" />
          <span className="font-[var(--font-mono)] text-[10px] text-[var(--ink-3)]">
            DAY <span className="font-bold text-[var(--ink-0)]">{data.day}</span>
            <span className="text-[var(--ink-4)]"> / {data.trialLength}</span>
          </span>
        </div>

        <div className="mb-1 flex items-baseline gap-2">
          <span className="font-[var(--font-display)] text-[76px] font-extrabold leading-[0.85] tracking-[-0.05em] tabular-nums text-[var(--ink-0)]">
            {data.callsHandled}
          </span>
          <span className="text-[14px] font-semibold text-[var(--ink-3)]">calls</span>
        </div>
        <div className="mb-[18px] text-[12.5px] tracking-[-0.005em] text-[var(--ink-3)]">
          handled by Elva — <span className="font-semibold text-[var(--ink-0)]">{pct}% completed without staff</span>
        </div>

        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[10px] border border-[var(--cream-2)] bg-[var(--cream-2)]">
          <div className="bg-[var(--cream-1)] px-[13px] py-[11px]">
            <div className="mb-1 font-[var(--font-mono)] text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Completed
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-[var(--font-display)] text-[22px] font-extrabold leading-none tabular-nums text-[var(--ai-3)]">
                {data.completedByElva}
              </span>
              <Icon kind="up" size={10} stroke={2.5} className="text-[var(--ai-3)]" />
            </div>
          </div>
          <div className="bg-[var(--cream-1)] px-[13px] py-[11px]">
            <div className="mb-1 font-[var(--font-mono)] text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Escalated
            </div>
            <span className="font-[var(--font-display)] text-[22px] font-extrabold leading-none tabular-nums text-[var(--ink-0)]">
              {data.safelyEscalated}
            </span>
          </div>
          <div className="bg-[var(--cream-1)] px-[13px] py-[11px]">
            <div className="mb-1 font-[var(--font-mono)] text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Hours saved
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-[var(--font-display)] text-[22px] font-extrabold leading-none tabular-nums text-[var(--ink-0)]">
                {data.hoursSaved}
              </span>
              <span className="text-[11px] font-semibold text-[var(--ink-3)]">hrs</span>
            </div>
          </div>
          <div className="bg-[var(--cream-1)] px-[13px] py-[11px]">
            <div className="mb-1 font-[var(--font-mono)] text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              To review
            </div>
            <span className="font-[var(--font-display)] text-[22px] font-extrabold leading-none tabular-nums text-[var(--terracotta)]">
              {data.needsAttention}
            </span>
          </div>
        </div>

        <div className="mt-4 flex-1" />

        <div className="mt-4">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="font-[var(--font-mono)] text-[10px] text-[var(--ink-3)]">Trial progress</span>
            <div className="flex-1" />
            <span className="font-[var(--font-mono)] text-[10px] font-semibold text-[var(--ink-2)]">
              {Math.round((data.day / data.trialLength) * 100)}%
            </span>
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-[var(--surface-mute)]">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(data.day / data.trialLength) * 100}%`,
                background: 'linear-gradient(120deg, var(--ai-3) 0%, var(--ai-4) 50%, var(--ai-5) 100%)',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TrialAttentionRow({ item, isLast }: { item: AttentionItem; isLast: boolean }) {
  return (
    <a
      href="#"
      onClick={(e) => e.preventDefault()}
      className={[
        'grid cursor-pointer items-center gap-4 px-5 py-[13px] text-inherit no-underline transition-colors hover:bg-[var(--surface-mute)]',
        'grid-cols-[auto_minmax(120px,1fr)_minmax(220px,2fr)_auto_12px]',
        isLast ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <PriorityBadge priority={item.priority} />
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">{item.patient}</div>
        <div className="mt-0.5 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-3)]">
          via {item.source.toLowerCase()}
        </div>
      </div>
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium tracking-[-0.005em] text-[var(--ink-0)]">{item.summary}</div>
        <div className="mt-0.5 truncate text-[11.5px] text-[var(--ink-3)]">{item.detail}</div>
      </div>
      <div
        className={[
          'text-right font-[var(--font-mono)] text-[11px] tracking-[0.02em]',
          item.due === 'Now' ? 'font-bold uppercase text-[var(--terracotta)]' : 'font-medium text-[var(--ink-3)]',
        ].join(' ')}
      >
        {item.due}
      </div>
      <Icon kind="right" size={12} stroke={2} className="text-[var(--ink-4)]" />
    </a>
  );
}

const REVIEW_ICON_MAP: Record<
  TrialReviewCall['icon'],
  { kind: IconKind; color: string }
> = {
  check: { kind: 'check-circ', color: 'var(--ai-3)' },
  shield: { kind: 'shield-check', color: 'var(--gold)' },
  book: { kind: 'book', color: 'var(--gold)' },
  user: { kind: 'team', color: 'var(--ink-2)' },
};

function TrialReviewIcon({ icon }: { icon: TrialReviewCall['icon'] }) {
  const cfg = REVIEW_ICON_MAP[icon];
  return (
    <div
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border"
      style={{
        background: `color-mix(in srgb, ${cfg.color} 13%, transparent)`,
        borderColor: `color-mix(in srgb, ${cfg.color} 20%, transparent)`,
        color: cfg.color,
      }}
    >
      <Icon kind={cfg.kind} size={15} stroke={2} />
    </div>
  );
}

function TrialReviewRow({ call, isLast }: { call: TrialReviewCall; isLast: boolean }) {
  return (
    <a
      href="#"
      onClick={(e) => e.preventDefault()}
      className={[
        'flex cursor-pointer items-center gap-3.5 px-5 py-[13px] text-inherit no-underline transition-colors hover:bg-[var(--surface-mute)]',
        isLast ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <TrialReviewIcon icon={call.icon} />
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5">
          <span className="font-[var(--font-mono)] text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-4)]">
            {call.kind}
          </span>
          <span className="text-[var(--ink-4)]">·</span>
          <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-3)]">{call.duration}</span>
        </div>
        <div className="truncate text-[13px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">{call.title}</div>
        <div className="mt-px truncate text-[11.5px] text-[var(--ink-3)]">{call.detail}</div>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 text-[11.5px] font-semibold text-[var(--ink-2)]">
        <Icon kind="headphones" size={12} stroke={2} />
        Review
      </span>
      <Icon kind="right" size={12} stroke={2} className="shrink-0 text-[var(--ink-4)]" />
    </a>
  );
}

function WorkCompletedRow({ item, isLast, max }: { item: WorkCompletedItem; isLast: boolean; max: number }) {
  const pct = (item.count / max) * 100;
  return (
    <div
      className={[
        'grid grid-cols-[auto_1fr_auto] items-center gap-3 px-[18px] py-3',
        isLast ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <div className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg border border-[var(--ai-tint)] bg-[var(--ai-tint)] text-[var(--ai-3)]">
        <Icon kind={item.icon} size={13} stroke={2} />
      </div>
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">{item.label}</div>
        <div className="mt-0.5 text-[10.5px] text-[var(--ink-3)]">{item.sub}</div>
        <div className="mt-[7px] h-[3px] w-full overflow-hidden rounded-full bg-[var(--surface-mute)]">
          <div className="h-full rounded-full bg-[var(--ai-3)] opacity-60" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="min-w-9 text-right font-[var(--font-display)] text-[26px] font-extrabold leading-none tracking-[-0.03em] tabular-nums text-[var(--ink-0)]">
        {item.count}
      </span>
    </div>
  );
}

function SafetyCell({ label, count, color }: { label: string; count: number; color: 'positive' | 'warning' | 'brass' }) {
  const styles =
    color === 'warning'
      ? { bg: 'var(--gold-tint)', border: 'color-mix(in srgb, var(--gold) 15%, transparent)', text: 'var(--gold)' }
      : color === 'brass'
        ? { bg: 'var(--gold-tint)', border: 'color-mix(in srgb, var(--gold) 15%, transparent)', text: 'var(--gold)' }
        : { bg: 'var(--ai-tint)', border: 'color-mix(in srgb, var(--ai-3) 15%, transparent)', text: 'var(--ai-3)' };

  return (
    <div
      className="flex flex-col gap-1 overflow-hidden rounded-[11px] border px-3.5 pb-3 pt-3.5"
      style={{ background: styles.bg, borderColor: styles.border }}
    >
      <span
        className="font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.03em] tabular-nums"
        style={{ color: styles.text }}
      >
        {count}
      </span>
      <div className="text-[11px] font-medium leading-snug tracking-[-0.005em] text-[var(--ink-2)]">{label}</div>
    </div>
  );
}

function TrialProgressSection() {
  const data = RCPT_TRIAL;
  const pct = (data.day / data.trialLength) * 100;
  const r = 44;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;

  return (
    <section className="relative mb-3.5 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-md">
      <div className="rcpt-empty-aurora-2 opacity-20" />
      <DashboardSectionHead index="06" title={`Your first ${data.trialLength} days`} accentColor="var(--ai-2)" />
      <div className="relative z-[1] px-[18px] py-4 pb-[18px]">
        <div className="mb-4 flex items-center gap-4">
          <div className="relative h-[100px] w-[100px] shrink-0">
            <svg width="100" height="100" viewBox="0 0 100 100" className="-rotate-90">
              <circle cx="50" cy="50" r={r} stroke="var(--surface-mute)" strokeWidth="6" fill="none" />
              <circle
                cx="50"
                cy="50"
                r={r}
                stroke={`url(#${TRIAL_GRAD_ID})`}
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${c}`}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em] tabular-nums text-[var(--ink-0)]">
                {data.day}
              </span>
              <span className="mt-0.5 font-[var(--font-mono)] text-[9.5px] text-[var(--ink-3)]">
                / {data.trialLength} days
              </span>
            </div>
          </div>
          <div className="min-w-0">
            <div className="mb-1 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Trial summary
            </div>
            <div className="mb-1 font-[var(--font-display)] text-[18px] font-bold leading-tight tracking-[-0.025em] text-[var(--ink-0)]">
              <span className="rcpt-empty-iri-text">{data.callsHandled} conversations</span> so far
            </div>
            <div className="text-[12px] leading-snug tracking-[-0.005em] text-[var(--ink-3)]">
              We&apos;ll wrap a complete picture at day {data.trialLength}.
            </div>
          </div>
        </div>

        <div className="mb-2 font-[var(--font-mono)] text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Your summary will include
        </div>
        <ul className="mb-3.5 grid list-none grid-cols-2 gap-x-2 gap-y-1 p-0">
          {RCPT_TRIAL_PROGRESS_INCLUDES.map((item) => (
            <li key={item} className="flex items-center gap-1.5 text-[11.5px] text-[var(--ink-2)]">
              <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-[var(--ai-tint)] text-[var(--ai-3)]">
                <Icon kind="check" size={7} stroke={2.5} />
              </span>
              {item}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="inline-flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border border-[var(--ai-3)] bg-[var(--ai-3)] text-[13px] font-semibold text-white shadow-[0_0_0_1px_var(--ai-tint),0_6px_18px_var(--ai-tint)]"
        >
          View trial progress
          <Icon kind="right" size={12} stroke={2} />
        </button>
      </div>
    </section>
  );
}

export function TrialDashboard() {
  const data = RCPT_TRIAL;
  const pct = Math.round((data.completedByElva / data.callsHandled) * 100);
  const workMax = Math.max(...RCPT_WORK_COMPLETED.map((i) => i.count));
  const workTotal = RCPT_WORK_COMPLETED.reduce((s, r) => s + r.count, 0);

  return (
    <>
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <linearGradient id={TRIAL_GRAD_ID} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="38%" stopColor="#EC4899" />
            <stop offset="68%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#10B981" />
          </linearGradient>
        </defs>
      </svg>

      <div className="mb-[18px] flex items-stretch gap-3.5">
        <LiveCallHero />
        <SinceLaunchTotals />
      </div>

      <div className="mb-[18px] grid grid-cols-4 gap-3">
        <DashboardKpiCell
          label="Calls handled"
          value={data.callsHandled}
          sub="all conversations since launch"
          delta={seriesTailDelta(data.series.calls)}
          deltaLabel="today"
          color="var(--ink-2)"
          icon="phone"
        />
        <DashboardKpiCell
          label="Completed by Elva"
          value={data.completedByElva}
          sub={`${pct}% resolved without staff`}
          delta={seriesTailDelta(data.series.completed)}
          deltaLabel="today"
          color="var(--ai-3)"
          icon="check-circ"
          accent
        />
        <DashboardKpiCell
          label="Safely escalated"
          value={data.safelyEscalated}
          sub="routed to your team"
          delta={seriesTailDelta(data.series.escalated)}
          deltaLabel="today"
          color="var(--gold)"
          icon="shield-check"
          accent
        />
        <DashboardKpiCell
          label="Needs attention"
          value={data.needsAttention}
          sub={data.needsAttention === 1 ? 'open item' : 'open items'}
          delta={seriesTailDelta(data.series.attention)}
          deltaLabel="today"
          color="var(--terracotta)"
          icon="alert"
          accent
        />
      </div>

      <div className="grid items-start gap-3.5 [grid-template-columns:minmax(0,1.5fr)_minmax(0,1fr)]">
        <div>
          <section className="mb-3.5 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-md">
            <DashboardSectionHead
              index="01"
              title="Needs staff attention"
              subtitle="Items Elva flagged for human follow-up."
              accentColor="var(--terracotta)"
              action={
                <a
                  href="/warm-transfer"
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--ai-3)] no-underline"
                >
                  Open Triage <Icon kind="right" size={10} stroke={2} />
                </a>
              }
            />
            <div>
              {RCPT_NEEDS_ATTENTION.map((item, i) => (
                <TrialAttentionRow key={item.id} item={item} isLast={i === RCPT_NEEDS_ATTENTION.length - 1} />
              ))}
            </div>
          </section>

          <section className="mb-3.5 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-md">
            <DashboardSectionHead
              index="02"
              title="Calls worth reviewing"
              subtitle="Curated by Elva — the four that tell you the most about how she's doing."
              accentColor="var(--ai-3)"
              action={
                <DashboardPill variant="brass">
                  <Icon kind="sparkle" size={9} stroke={2} />
                  Hand-picked
                </DashboardPill>
              }
            />
            <div>
              {RCPT_CALLS_WORTH_REVIEWING.map((call, i) => (
                <TrialReviewRow
                  key={call.id}
                  call={call}
                  isLast={i === RCPT_CALLS_WORTH_REVIEWING.length - 1}
                />
              ))}
            </div>
          </section>

          <section className="mb-3.5 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-md">
            <DashboardSectionHead
              index="03"
              title="Early patterns Elva noticed"
              subtitle="What's emerging from the conversations so far."
              accentColor="var(--gold)"
              action={
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--ai-3)] no-underline"
                >
                  Insights <Icon kind="right" size={10} stroke={2} />
                </a>
              }
            />
            <div className="px-5 pb-3.5 pt-1.5">
              {RCPT_EARLY_PATTERNS.map((pattern, i) => (
                <a
                  key={pattern.id}
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className={[
                    'grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-3.5 py-3 text-inherit no-underline',
                    i < RCPT_EARLY_PATTERNS.length - 1 ? 'border-b border-dashed border-[var(--cream-2)]' : '',
                  ].join(' ')}
                >
                  <span className="min-w-9 font-[var(--font-display)] text-[26px] font-extrabold leading-none tracking-[-0.04em] tabular-nums text-[var(--ink-0)]">
                    {pattern.count}
                  </span>
                  <div className="text-[12.5px] font-medium leading-normal tracking-[-0.005em] text-[var(--ink-0)]">
                    {pattern.text}
                  </div>
                  <Icon kind="external" size={12} stroke={2} className="text-[var(--ink-4)]" />
                </a>
              ))}
            </div>
          </section>
        </div>

        <div>
          <section className="mb-3.5 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-md">
            <DashboardSectionHead
              index="04"
              title="Work completed"
              subtitle="What Elva delivered without staff."
              accentColor="var(--ai-3)"
              action={
                <DashboardPill>
                  <Icon kind="check" size={9} stroke={2.5} />
                  {workTotal} done
                </DashboardPill>
              }
            />
            <div>
              {RCPT_WORK_COMPLETED.map((item, i) => (
                <WorkCompletedRow
                  key={item.id}
                  item={item}
                  isLast={i === RCPT_WORK_COMPLETED.length - 1}
                  max={workMax}
                />
              ))}
            </div>
          </section>

          <section className="mb-3.5 overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-md">
            <DashboardSectionHead
              index="05"
              title="Safety checks"
              subtitle="Where Elva chose caution."
              accentColor="var(--gold)"
              action={<Icon kind="shield-check" size={14} stroke={2} className="text-[var(--ai-3)]" />}
            />
            <div className="grid grid-cols-2 gap-2 px-3.5 pb-3.5 pt-3">
              {RCPT_SAFETY_CHECKS.map((check) => (
                <SafetyCell key={check.id} label={check.label} count={check.count} color={check.color} />
              ))}
            </div>
          </section>

          <TrialProgressSection />
        </div>
      </div>
    </>
  );
}
